import ChatBotAvatar from '@/components/Chat/ChatBotAvatar';
import React, { useEffect, useRef, useState } from 'react';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { usePathname, useRouter } from 'expo-router';
import Icon from '@/components/Icon';
import { chatsApi } from '@/lib/api/chats';
import { displayChatTitle } from '@/lib/chatTitle';

// The compact navigation rail is used by the desktop web layout.
export default function RailRecentChats({ colors }) {
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [chats, setChats] = useState([]);
  const [status, setStatus] = useState('loading');
  const [hovered, setHovered] = useState(false);
  const closeTimer = useRef(null);
  const openedByHover = useRef(false);
  const contentRef = useRef(null);

  const cancelClose = () => clearTimeout(closeTimer.current);
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      if (openedByHover.current || !contentRef.current?.contains(document.activeElement)) setOpen(false);
    }, 180);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStatus('loading');
    // Avoid changing the Library's filters or replacing its current results.
    chatsApi.fetchChats(1, { filter: 'all', sort: 'newest' }).then(({ chats: results }) => {
      if (cancelled) return;
      setChats((results || []).filter(chat => chat.id
        && !chat.isArchived && !chat.flags?.isArchived
        && !chat.flags?.isTemporary && !chat.flags?.isDeleted
        && !(chat.flags?.isToolRun && !chat.flags?.isToolPromotedToChat))
        .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0))
        .slice(0, 10));
      setStatus('ready');
    }).catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [open]);

  return (
    <Menu.Root open={open} onOpenChange={setOpen} modal={false}>
      <Menu.Trigger asChild>
        <button
          type="button"
          aria-label="Recent chats"
          onPointerEnter={event => {
            if (event.pointerType !== 'mouse') return;
            cancelClose();
            setHovered(true);
            if (!open) openedByHover.current = true;
            setOpen(true);
          }}
          onPointerLeave={() => { setHovered(false); scheduleClose(); }}
          onPointerDown={event => {
            if (open && openedByHover.current) event.preventDefault();
            openedByHover.current = false;
          }}
          onKeyDown={() => { openedByHover.current = false; cancelClose(); }}
          style={{
            width: 44, height: 44, border: 0, borderRadius: 11, padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            background: open || hovered ? colors.solid_color_tertiary : 'transparent',
          }}
        >
          <Icon name="messages" size={19} color={open ? colors.text_color_primary : colors.text_color_secondary} />
        </button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content
          ref={contentRef}
          aria-label="Recent chats"
          side="right" align="start" sideOffset={6} collisionPadding={12}
          onPointerEnter={cancelClose} onPointerLeave={scheduleClose}
          onOpenAutoFocus={event => { if (openedByHover.current) event.preventDefault(); }}
          onCloseAutoFocus={event => { if (openedByHover.current) event.preventDefault(); }}
          className="DropdownMenuContent"
          style={{
            width: 300, minWidth: 0, maxWidth: 'calc(100vw - 84px)',
            maxHeight: 'min(560px, var(--radix-dropdown-menu-content-available-height))',
            overflowY: 'auto', boxSizing: 'border-box', padding: 10, borderRadius: 20,
            background: colors.solid_color_secondary, color: colors.text_color_primary,
            border: `1px solid ${colors.border_color_secondary}`, zIndex: 10000,
          }}
        >
          <Menu.Label style={{ padding: '10px 12px', fontSize: 13, color: colors.text_color_tertiary }}>Recents</Menu.Label>
          {status !== 'ready' || chats.length === 0 ? (
            <div role="status" style={{ padding: '12px', fontSize: 14, color: colors.text_color_secondary }}>
              {status === 'loading' ? 'Loading recent chats…' : status === 'error' ? 'Couldn’t load recent chats.' : 'No recent chats yet.'}
            </div>
          ) : chats.map(chat => (
            <Menu.Item key={chat.id} textValue={displayChatTitle(chat.title)} onSelect={() => router.push(`/c/${chat.id}`)}
                className="DropdownMenuItem"
                title={displayChatTitle(chat.title)}
                style={{ display: 'flex', alignItems: 'center', minHeight: 40, padding: '10px 12px', borderRadius: 9, color: colors.text_color_primary, fontSize: 14, textDecoration: 'none' }}>
                <ChatBotAvatar agentId={chat.agentId} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayChatTitle(chat.title)}</span>
            </Menu.Item>
          ))}
          <Menu.Separator style={{ height: 1, background: colors.border_color_secondary, margin: '6px 4px' }} />
          <Menu.Item className="DropdownMenuItem" onSelect={() => router.push('/library')}
            style={{ display: 'flex', alignItems: 'center', minHeight: 40, padding: '10px 12px', borderRadius: 9, fontSize: 14 }}>
            View all chats
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
